import { COLORS } from '../../constants/theme';
import { ATTACK_TYPES } from '../../constants/attack';
import type { AttackType } from '../../types/attack';

interface AttackTypeSelectorProps {
  selectedAttackType: AttackType;
  onAttackTypeChange: (type: AttackType) => void;
}

/**
 * SF-174 Component 3: extracted from the `<select>` previously embedded in each shell's commit
 * bar — rendered once in `Attack.tsx` between `TargetSelector` and `WarRoomTargetCard`. A
 * segmented `role="radiogroup"`/`role="radio"` row (matching the existing Generals control and
 * mode-toggle precedent), since the domain is small (6 values) and every option benefits from
 * being visible/tappable without opening a dropdown.
 */
export function AttackTypeSelector({ selectedAttackType, onAttackTypeChange }: AttackTypeSelectorProps) {
  const types = Object.values(ATTACK_TYPES).filter((t) => t.id !== 'AMBUSH');

  return (
    <div
      role="radiogroup"
      aria-label="Attack type"
      className="flex flex-wrap gap-1.5"
    >
      {types.map((type) => {
        const isSelected = type.id === selectedAttackType;
        return (
          <button
            key={type.id}
            type="button"
            role="radio"
            aria-checked={isSelected}
            disabled={!type.enabled}
            onClick={() => onAttackTypeChange(type.id as AttackType)}
            className="flex-1 rounded text-sm font-semibold px-2"
            style={{
              minHeight: 44,
              minWidth: 140,
              border: `1px solid ${isSelected ? COLORS.link : COLORS.border}`,
              backgroundColor: isSelected ? 'rgba(106, 158, 142, 0.15)' : COLORS.sectionHeader,
              color: !type.enabled ? COLORS.text.muted : isSelected ? COLORS.link : COLORS.text.secondary,
              cursor: type.enabled ? 'pointer' : 'not-allowed',
            }}
          >
            {type.name}{!type.enabled ? ' (Soon)' : ''}
          </button>
        );
      })}
    </div>
  );
}
